import React, { useEffect, useState, useRef, useContext } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { motion } from "framer-motion";
import { PostHogProvider } from "./shared/providers/PostHogProvider";
import { SupabaseProvider } from "./shared/supabase/supabase";
import {
  AuthProvider,
  useAuth,
} from "./Authentication/components/RealTimeAuthStatus";
import { UserProfileProvider } from "./shared/providers/UserProfileProvider";
import { RemainingCreditsProvider, RemainingCreditsContext } from "./shared/providers/RemainingCreditsProvider";
import { AuthPromptProvider } from "./shared/contexts/AuthPromptContext";
import { RatingModalProvider } from "./shared/contexts/RatingModalContext";
import { AnalyticsIdentifier } from "./shared/components/AnalyticsIdentifier";
import AppSumoReviewBanner from "./shared/components/AppSumoReviewBanner";
import { Dashboard } from "./Dashboard";
import EditorPage from "./Editor/EditorPage";
import { AuthService } from "./Authentication/components/AuthService";
import supabase from "./shared/supabase/supabase";
import { DocumentService } from "./shared/services/DocumentService";
import "./App.css";

// Auth callback handler component
const AuthCallback: React.FC = () => {
  useEffect(() => {
    const handleCallback = async () => {
      const authService = AuthService.getInstance();
      await authService.handleOAuthCallback(supabase);

      // AppInitializer will handle syncing on SIGNED_IN event
      // Just redirect to dashboard
      window.location.href = "/dashboard";
    };

    handleCallback();
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-light text-gray-900">Authenticating...</h2>
        <p className="text-sm text-gray-500 mt-2">
          Please wait while we log you in
        </p>
      </div>
    </div>
  );
};

// Optional auth route wrapper - allows both authenticated and anonymous users
const OptionalAuthRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-light text-gray-900">Loading...</h2>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Component to handle initial sync on app load
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const syncCheckedRef = useRef(false);
  const { refreshCredits } = useContext(RemainingCreditsContext);

  useEffect(() => {
    const checkAndSync = async (user: any) => {
      try {
        // FIRST check if there are local documents - no point doing anything if nothing to sync
        const localDocs = DocumentService.getLocalDocuments();

        if (localDocs.length === 0) {
          // No local documents, nothing to sync - exit early
          return;
        }

        if (user) {
          // Check if we already synced to prevent duplicates
          if (syncCheckedRef.current) return;
          syncCheckedRef.current = true; // Set flag RIGHT BEFORE sync

          setIsSyncing(true);

          const syncResult = await DocumentService.syncToCloud(user.id);

          setIsSyncing(false);

          // Refresh credits after successful sync
          if (syncResult.success) {
            await refreshCredits();
          }

          // Reload to refresh the UI with synced documents
          if (syncResult.synced > 0) {
            window.location.reload();
          }
        }
      } catch (error) {
        console.error("Error during sync:", error);
        setIsSyncing(false);
      }
    };

    // Check on mount
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) checkAndSync(user);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        // Reset the flag when user signs in so sync can happen
        syncCheckedRef.current = false;
        checkAndSync(session.user);
      }
    });

    return () => subscription.unsubscribe();
  }, []); // Empty dependency array - only run once on mount

  if (isSyncing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-sky-50/30 flex items-center justify-center"
      >
        {/* Animated background orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.3, 0.2],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-80 h-80 bg-sky-200/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.2, 0.35, 0.2],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
            className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200/20 rounded-full blur-3xl"
          />
        </div>

        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 20,
          }}
          className="relative"
        >
          {/* Glass card */}
          <div
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-12 shadow-2xl border border-white/50"
            style={{
              boxShadow:
                "0 20px 40px -10px rgba(14, 165, 233, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset",
            }}
          >
            {/* Animated sync icon */}
            <div className="relative w-24 h-24 mx-auto mb-8">
              {/* Outer ring */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute inset-0 rounded-full border-4 border-sky-100"
              />

              {/* Rotating gradient ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "linear",
                }}
                className="absolute inset-0 rounded-full"
              >
                <div
                  className="h-full w-full rounded-full border-4 border-transparent"
                  style={{
                    borderTopColor: "rgb(14, 165, 233)",
                    borderRightColor: "rgba(14, 165, 233, 0.3)",
                  }}
                />
              </motion.div>

              {/* Center icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="w-12 h-12 bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center shadow-lg"
                >
                  {/* Cloud sync icon with Lucide */}
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 16V12m0 0l3 3m-3-3l-3 3m13-3v4m0 0l-3-3m3 3l3-3M15 4h3a2 2 0 012 2v2M5 8V6a2 2 0 012-2h3"
                    />
                  </svg>
                </motion.div>
              </div>
            </div>

            {/* Text content */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-center max-w-sm"
            >
              <h2 className="text-2xl font-light text-gray-900 mb-2">
                Syncing your documents
              </h2>
              <p className="text-sm text-gray-500 leading-relaxed">
                We're securely saving your progress to the cloud
              </p>

              {/* Progress dots */}
              <div className="flex items-center justify-center space-x-2 mt-6">
                {[0, 150, 300].map((delay) => (
                  <motion.div
                    key={delay}
                    animate={{
                      y: [0, -8, 0],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      delay: delay / 1000,
                      ease: "easeInOut",
                    }}
                    className="w-2 h-2 bg-sky-400 rounded-full"
                  />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Subtle shadow underneath */}
          <motion.div
            animate={{
              scale: [0.9, 1, 0.9],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-4 left-8 right-8 h-4 bg-black/5 rounded-full blur-xl"
          />
        </motion.div>
      </motion.div>
    );
  }

  return <>{children}</>;
};

function App() {
  return (
    <PostHogProvider>
      <SupabaseProvider>
        <AuthProvider>
          <UserProfileProvider>
            <RemainingCreditsProvider>
              <AnalyticsIdentifier>
                <AuthPromptProvider>
                <RatingModalProvider>
                <AppInitializer>
                  <Router>
                    <AppSumoReviewBanner />
                    <Routes>
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route path="/auth/callback" element={<AuthCallback />} />
                      <Route
                        path="/dashboard"
                        element={
                          <OptionalAuthRoute>
                            <Dashboard />
                          </OptionalAuthRoute>
                        }
                      />
                      <Route
                        path="/editor"
                        element={
                          <OptionalAuthRoute>
                            <EditorPage />
                          </OptionalAuthRoute>
                        }
                      />
                    </Routes>
                  </Router>
                </AppInitializer>
                </RatingModalProvider>
                </AuthPromptProvider>
              </AnalyticsIdentifier>
            </RemainingCreditsProvider>
          </UserProfileProvider>
        </AuthProvider>
      </SupabaseProvider>
    </PostHogProvider>
  );
}

export default App;
